import { JobPostingCatchphraseComponent } from '@/components/JobPosting/JobPostingCatchphrase'
import { JobPostingTitleComponent } from '@/components/JobPosting/JobPostingTitle'
import CardLabels from '@/components/JobPosting/Labels'
import { Block } from '@/components/base/Block'
import { PrimaryButton, SubButton } from '@/components/base/Button'
import { buttonSize } from '@/components/base/Button/Base'
import { Heading3 } from '@/components/base/Heading'
import { Icon, type LocalIconType } from '@/components/base/Icon'
import FavoriteIcon from '@/components/base/Icon/FavoriteIcon'
import { Col, Row } from '@/components/base/Layout'
import { Text3, Text4 } from '@/components/base/Text'
import Text5 from '@/components/base/Text/Text5'
import {
  jobPostingServicePostActionMutationKey,
  useJobPostingServicePostAction
} from '@/feature/JobPosting/api/postJobPostingJobSeekerAction'
import {
  showErrorMessageActions,
  showMessageActions
} from '@/feature/JobPosting/common'
import { useApp, type AppTheme } from '@/hooks/useApp'
import useIsMobile from '@/hooks/useMobile'
import { borderLine, borderSize } from '@/styles/border'
import borderRadius from '@/styles/border_radius'
import { align, display } from '@/styles/display'
import { fontSize, fontWeight } from '@/styles/font'
import spaceStyle from '@/styles/space'
import textAlign from '@/styles/text_align'
import { useIsMutating } from '@tanstack/react-query'
import {
  type Labelsv1Label,
  type V1AppColorTheme,
  type V1IconType,
  V1JobPostingActionType,
  type V1JobPostingCardAttachmentInfo,
  type V1Tag
} from 'mnvm-mypage-app-ts-if'
import { useEffect, useState } from 'react'
import styled from 'styled-components'
import CardTags from '../Tags'
import RecommendLabel from './RecommendLabel'
import DisabledJobPostingPopUp from '@/components/base/PopUp/disabledJobPostingPopUp'
import { isInstanceOfResponseError } from '@/utils/error'
import AdvisorCommentContent from './AdvisorCommentContent'
import { isEmpty } from 'lodash'
import { JobPostingMemoInput } from '@/components/JobPosting/JobPostingMemo'

const StyledImageContainer = styled.div`
  border-radius: 12px 12px 0 0;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: auto;
`

interface FirstSectionProps {
  theme: AppTheme
  labels: Labelsv1Label[]
  smallTitle: string
  shopName: string
  title: string
  tags: V1Tag[]
}

export interface PayloadFavorite {
  job_posting_id: string
  status: boolean
  source?: string
}

const FirstSection = ({
  theme,
  labels,
  smallTitle,
  shopName,
  title,
  tags
}: FirstSectionProps): JSX.Element => {
  return (
    <Block margin={{ top: spaceStyle.L }}>
      <CardLabels items={labels} />
      <Block textAlign="left" borderBottom margin={{ bottom: spaceStyle.S }}>
        <JobPostingTitleComponent
          isCard
          title={title}
          smallTitle={`${smallTitle}\u3000${shopName}`}
        />
        <div style={{ display: 'flex', flexWrap: 'wrap', marginBottom: '8px' }}>
          <CardTags tags={tags} />
        </div>
      </Block>
    </Block>
  )
}

interface JobInformationItem {
  iconType?: LocalIconType | V1IconType
  title: string
  text: string
}

const JobInformation = ({
  iconType,
  title,
  text
}: JobInformationItem): JSX.Element => {
  const app = useApp()

  return (
    <Block padding={{ vertical: spaceStyle.XS }}>
      <Block
        display={display.FLEX}
        alignItems={align.CENTER}
        textAlign={textAlign.LEFT}
      >
        <Block width="100px">
          <Block
            display={display.FLEX}
            margin={{ right: spaceStyle.M }}
            alignItems={align.CENTER}
            justifyContent={align.LEFT}
          >
            <Icon
              iconType={iconType ?? 'ICON_TYPE_HOME'}
              iconStyle={{
                fontSize: fontSize.L,
                theme: app.theme.primaryMainColor,
                margin: { right: spaceStyle.XS }
              }}
            />
            <Text4
              fontWeight={fontWeight.Bold}
              margin={{ vertical: spaceStyle.S }}
            >
              {title}
            </Text4>
          </Block>
        </Block>
        <Block
          width="243px"
          padding={{ right: spaceStyle.XS }}
          textAlign={textAlign.LEFT}
        >
          {text.split('\n').map((value, key) => {
            return (
              <Text4
                key={key}
                textColor={app.theme.subTextColor}
                margin={{ all: spaceStyle.NONE }}
              >
                {value}
              </Text4>
            )
          })}
        </Block>
      </Block>
    </Block>
  )
}

interface MiddleSectionProps {
  theme: AppTheme
  attachmentInfo?: V1JobPostingCardAttachmentInfo[]
  recommendComment: string
  code: string
  update: string
}
const MiddleSection = ({
  theme,
  attachmentInfo,
  recommendComment,
  code,
  update
}: MiddleSectionProps): JSX.Element => {
  return (
    <Block
      borderBottom={{
        size: borderSize.S,
        line: borderLine.Solid,
        color: theme.frameColor
      }}
    >
      {attachmentInfo
        ?.filter((e) => e.text)
        .map((e) => {
          return (
            <JobInformation
              key={e.text}
              iconType={e.iconType}
              title={e.title ?? ''}
              text={e.text ?? ''}
            />
          )
        })}
      {recommendComment != null && recommendComment !== '' && (
        <Block margin={{ vertical: spaceStyle.S }}>
          <JobPostingCatchphraseComponent>
            {recommendComment}
          </JobPostingCatchphraseComponent>
        </Block>
      )}
      <Text5
        textColor={theme.subTextColor}
        textAlign={textAlign.LEFT}
        margin={{ top: spaceStyle.NONE, bottom: spaceStyle.XS }}
      >
        求人番号：{code} 更新日：{update}
      </Text5>
    </Block>
  )
}

interface BottomActionSectionProps {
  jobPostingId?: string
  theme: AppTheme
  isShowActionButton: boolean
  isDeclinedApplication: boolean
  onClickShowDetailButton: () => void
  isLiked?: boolean
  showDetailView: boolean
  isConsidering: boolean
}
const BottomActionSection = ({
  jobPostingId,
  theme,
  isShowActionButton,
  isDeclinedApplication,
  onClickShowDetailButton,
  isLiked,
  showDetailView,
  isConsidering
}: BottomActionSectionProps): JSX.Element => {
  const isMobile = useIsMobile()
  const id = jobPostingId ?? ''
  const [isDeclineButtonActive, setIsDeclineButtonActive] = useState(true)
  const [isApplyButtonActive, setIsApplyButtonActive] = useState(true)
  const [showDetailPopup, setShowDetailPopup] = useState(false)
  const [isConsideringButtonState, setIsConsideringButtonState] =
    useState(isConsidering)

  const handleShowDetail = (): void => {
    if (!showDetailView) {
      setShowDetailPopup(true)
    } else {
      onClickShowDetailButton()
    }
  }

  useEffect(() => {
    if (isDeclinedApplication) {
      setIsDeclineButtonActive(false)
    }
    return () => {
      setShowDetailPopup(false)
    }
  }, [])

  const useJobPostingServicePostActionMutation =
    useJobPostingServicePostAction()
  const isJobPostingServicePostActionMutating = Boolean(
    useIsMutating([jobPostingServicePostActionMutationKey])
  )

  const onClick = (actionType: V1JobPostingActionType): void => {
    if (
      actionType !== V1JobPostingActionType.Apply &&
      actionType !== V1JobPostingActionType.Decline &&
      actionType !== V1JobPostingActionType.Consider &&
      actionType !== V1JobPostingActionType.UnderConsider
    ) {
      return
    }

    if (isJobPostingServicePostActionMutating) {
      console.log('prevent JobPostingServicePostAction because in mutating')
      return
    }

    void useJobPostingServicePostActionMutation
      .mutateAsync({
        jobPostingId: id,
        body: {
          actionType
        }
      })
      .then(() => {
        showMessageActions(actionType, [])
      })
      .catch(async (err) => {
        if (isInstanceOfResponseError(err)) {
          const statusCode = err.response.status
          // show error message if status code is not 409
          if (statusCode !== 409) {
            showErrorMessageActions(actionType, [])
          }
        }
      })
  }

  const ActionButtonAndShowDetailButtonBlock = (
    <Block
      display={display.FLEX}
      maxWidth={'100%'}
      style={{ gap: isMobile ? spaceStyle.XS : spaceStyle.LL }}
    >
      <PrimaryButton
        width="50px"
        onClick={() => {
          if (isApplyButtonActive) {
            onClick(V1JobPostingActionType.Apply)
            setIsApplyButtonActive(false)
            setIsDeclineButtonActive(false)
          }
        }}
        style={{
          backgroundColor: isApplyButtonActive
            ? theme.primaryMainColor.color
            : theme.disabledColor.color
        }}
        height={buttonSize.M}
        padding={{
          horizontal: spaceStyle.M
        }}
      >
        <Heading3
          fixedSPfontSize
          fontSizeSP={isMobile ? fontSize.XXS : fontSize.S}
          textColor={theme.whiteTextColor}
          margin={{ all: spaceStyle.NONE }}
        >
          応募する
        </Heading3>
      </PrimaryButton>

      <PrimaryButton
        width="50px"
        onClick={() => {
          onClick(
            isConsideringButtonState
              ? V1JobPostingActionType.UnderConsider
              : V1JobPostingActionType.Consider
          )
          setIsConsideringButtonState(!isConsideringButtonState)
        }}
        style={{
          backgroundColor: theme.primaryMainColor.color,
          opacity: 0.4
        }}
        height={buttonSize.M}
        padding={{
          horizontal: spaceStyle.M
        }}
      >
        <Heading3
          fixedSPfontSize
          fontSizeSP={isMobile ? fontSize.XXS : fontSize.S}
          textColor={theme.whiteTextColor}
        >
          {isConsideringButtonState ? '検討中' : '検討する'}
        </Heading3>
      </PrimaryButton>

      <SubButton
        width="50px"
        onClick={() => {
          if (isDeclineButtonActive) {
            onClick(V1JobPostingActionType.Decline)
            setIsDeclineButtonActive(false)
          }
        }}
        style={{
          backgroundColor: isDeclineButtonActive
            ? theme.whiteTextColor.color
            : theme.disabledColor.color,
          color: isDeclineButtonActive
            ? theme.subTextColor.color
            : theme.whiteTextColor.color
        }}
        fontSize={isMobile ? fontSize.XXS : fontSize.S}
        padding={{
          vertical: spaceStyle.M,
          horizontal: spaceStyle.M
        }}
      >
        応募しない
      </SubButton>

      <SubButton
        width="50px"
        onClick={onClickShowDetailButton}
        fontSize={isMobile ? fontSize.XXS : fontSize.S}
        padding={{
          vertical: spaceStyle.M,
          horizontal: spaceStyle.M
        }}
      >
        詳細を見る
      </SubButton>
    </Block>
  )

  return (
    <>
      <Block
        margin={{
          horizontal: isMobile ? spaceStyle.XS : spaceStyle.M,
          vertical: spaceStyle.S
        }}
      >
        <Row>
          <Col span={22}>
            {isShowActionButton ? (
              ActionButtonAndShowDetailButtonBlock
            ) : (
              <>
                <Block display={display.FLEX} maxWidth={'100%'}>
                  <PrimaryButton
                    onClick={handleShowDetail}
                    height={buttonSize.M}
                  >
                    <Heading3 fixedSPfontSize textColor={theme.whiteTextColor}>
                      詳細を見る
                    </Heading3>
                  </PrimaryButton>
                </Block>

                {
                  <DisabledJobPostingPopUp
                    msg="この求人は現在、募集中止または公開停止依頼があったため表示することができません。ご不明な点がある場合はキャリアアドバイザーへお問い合わせくださいませ。"
                    display={showDetailPopup}
                    onClose={() => {
                      setShowDetailPopup(false)
                    }}
                  />
                }
              </>
            )}
          </Col>
          <Col span={2}>
            <FavoriteIcon
              id={id}
              marginProp={
                isMobile ? { vertical: spaceStyle.AUTO } : { all: spaceStyle.M }
              }
              isLiked={isLiked}
            />
          </Col>
        </Row>
      </Block>
    </>
  )
}

interface InformationLabelProps {
  isShow: boolean
  text: string
  backgroundColor?: V1AppColorTheme
  textColor: V1AppColorTheme
}

const InformationLabel = ({
  isShow,
  text,
  backgroundColor,
  textColor
}: InformationLabelProps): JSX.Element => {
  return isShow ? (
    <Block
      borderRadius={{
        upper: borderRadius.XL
      }}
      textAlign={align.CENTER}
      backgroundColor={backgroundColor}
      style={{ opacity: 0.4 }}
    >
      <Text3
        fontWeight={fontWeight.Bold}
        margin={{ vertical: spaceStyle.NONE }}
        padding={{ vertical: spaceStyle.S }}
        textColor={textColor}
      >
        {text}
      </Text3>
    </Block>
  ) : (
    <></>
  )
}

interface Props {
  jobPostingId?: string
  tags: V1Tag[]
  labels: Labelsv1Label[]
  title: string
  smallTitle: string
  shopName: string
  imageUrl?: string
  attachmentInfo?: V1JobPostingCardAttachmentInfo[]
  recommendComment: string
  code: string
  update: string
  isShowActionButton: boolean
  isAcceptedOffer: boolean
  isDeclinedApplication: boolean
  onClickShowDetailButton: () => void
  isLiked?: boolean
  isRecommended?: boolean
  showDetailView: boolean
  advisorComment?: string
  introductionDate?: string
  isConsidering?: boolean
  mpOrderMemo?: string
  isViewMemo?: boolean
}

const JobPostingCardType2 = ({
  jobPostingId,
  tags,
  labels,
  title,
  smallTitle,
  shopName,
  imageUrl,
  attachmentInfo,
  recommendComment,
  code,
  update,
  isShowActionButton,
  isAcceptedOffer,
  isDeclinedApplication,
  onClickShowDetailButton,
  isLiked,
  isRecommended = false,
  showDetailView,
  advisorComment,
  introductionDate,
  isConsidering = false,
  mpOrderMemo = '',
  isViewMemo = false
}: Props): JSX.Element => {
  const app = useApp()
  const isShowIntroductionDate = !isEmpty(introductionDate)

  return (
    <Block
      margin={{
        horizontal: spaceStyle.AUTO,
        top: isShowIntroductionDate ? spaceStyle.S : spaceStyle.XL
      }}
    >
      {isShowIntroductionDate && (
        <Block
          display={display.FLEX}
          justifyContent={align.FLEX_END}
          margin={{ bottom: spaceStyle.S }}
        >
          <Text3 margin={{ all: spaceStyle.NONE }}>
            紹介日：{introductionDate}
          </Text3>
        </Block>
      )}
      <Block
        border
        borderRadius={{ all: borderRadius.XL }}
        textAlign="left"
        backgroundColor={
          isDeclinedApplication ? app.theme.grayBackroundColor : undefined
        }
        style={{ boxSizing: 'border-box', position: 'relative' }}
      >
        <InformationLabel
          isShow={isConsidering}
          text={'検討中'}
          backgroundColor={app.theme.primaryMainColor}
          textColor={app.theme.whiteColor}
        />
        <InformationLabel
          isShow={isAcceptedOffer}
          text={'内定承諾済'}
          backgroundColor={app.theme.yellowColor}
          textColor={app.theme.mainTextColor}
        />
        <InformationLabel
          isShow={isDeclinedApplication}
          text={'応募辞退済'}
          textColor={app.theme.subTextColor}
        />
        <RecommendLabel
          isShow={isRecommended}
          isAbsolute={false}
          rounded={
            isConsidering || isAcceptedOffer || isDeclinedApplication
              ? borderRadius.NONE
              : borderRadius.XL
          }
        />
        <AdvisorCommentContent
          isShow={!isEmpty(advisorComment)}
          text={advisorComment ?? ''}
          padding={{
            horizontal: spaceStyle.XS,
            vertical: spaceStyle.M
          }}
          borderRadius={{
            upper:
              isConsidering ||
              isAcceptedOffer ||
              isDeclinedApplication ||
              isRecommended
                ? borderRadius.NONE
                : borderRadius.XL
          }}
        />
        {imageUrl !== undefined && imageUrl !== '' ? (
          <StyledImageContainer
            style={{
              borderRadius: '12px 12px 0 0 ',
              overflow: 'hidden'
            }}
          >
            <img
              src={imageUrl}
              alt="job posting image"
              style={{
                objectFit: 'contain',
                objectPosition: '50% 50%',
                width: '100%',
                height: '100%',
                maxWidth: '200px',
                maxHeight: '150px'
              }}
            />
          </StyledImageContainer>
        ) : (
          <></>
        )}
        <Block
          padding={{ horizontal: spaceStyle.S }}
          borderBottom
          borderRadius={{ lower: borderRadius.XL }}
        >
          <FirstSection
            theme={app.theme}
            labels={labels}
            smallTitle={smallTitle}
            shopName={shopName}
            title={title}
            tags={tags}
          />

          <MiddleSection
            theme={app.theme}
            attachmentInfo={attachmentInfo}
            recommendComment={recommendComment}
            code={code}
            update={update}
          />

          <BottomActionSection
            jobPostingId={jobPostingId}
            theme={app.theme}
            isShowActionButton={isShowActionButton}
            isDeclinedApplication={isDeclinedApplication}
            onClickShowDetailButton={onClickShowDetailButton}
            isLiked={isLiked}
            showDetailView={showDetailView}
            isConsidering={isConsidering}
          />

          {jobPostingId != null && jobPostingId !== '' && isViewMemo && (
            <JobPostingMemoInput
              jobPostingId={jobPostingId}
              initialMemo={mpOrderMemo}
            />
          )}
        </Block>
      </Block>
    </Block>
  )
}

export default JobPostingCardType2
